'use client'

import { useCallback, useEffect, useRef, type RefObject } from 'react'
import {
  collectZoomableImages,
  dedupeLightboxItems,
  findLightboxIndex,
  isDirectZoomableImageClick,
  isImageFileHref,
  isZoomableArticleImage,
  resolveLightboxSrc,
  type LightboxItem,
} from '@/lib/lightbox-image'
import { openFancybox } from '@/lib/open-fancybox'

const EMPTY_ITEMS: LightboxItem[] = []
const CLICK_THROUGH_MS = 500

function stopImageNavigation(event: Event) {
  event.preventDefault()
  event.stopPropagation()
  if ('stopImmediatePropagation' in event) {
    event.stopImmediatePropagation()
  }
}

export function useArticleImageLightbox(
  contentRef: RefObject<HTMLElement | null>,
  heroSrc: string | null,
  heroAlt = '',
  extraItems: LightboxItem[] = EMPTY_ITEMS,
  heroOriginEl?: RefObject<HTMLElement | null>,
) {
  const readyRef = useRef(false)

  useEffect(() => {
    readyRef.current = false
    const timer = window.setTimeout(() => {
      readyRef.current = true
    }, CLICK_THROUGH_MS)
    return () => window.clearTimeout(timer)
  }, [heroSrc])

  const buildItems = useCallback(() => {
    const contentItems = contentRef.current ? collectZoomableImages(contentRef.current) : []
    const heroItem = heroSrc ? [{ src: heroSrc, alt: heroAlt, title: heroAlt }] : []
    return dedupeLightboxItems([...heroItem, ...contentItems, ...extraItems])
  }, [contentRef, extraItems, heroAlt, heroSrc])

  const openAt = useCallback(
    (src?: string, originEl?: Element | null) => {
      if (!readyRef.current) return
      const items = buildItems()
      if (items.length === 0) return
      openFancybox(items, findLightboxIndex(items, src), originEl)
    },
    [buildItems],
  )

  useEffect(() => {
    const root = contentRef.current
    if (!root) return

    root.querySelectorAll('img').forEach((img) => {
      if (isZoomableArticleImage(img)) img.draggable = false
    })

    const onClick = (event: MouseEvent) => {
      if (!readyRef.current || !event.isTrusted || event.button !== 0) return
      const target = event.target
      if (!(target instanceof Element)) return

      const img = isDirectZoomableImageClick(target, root)
      const anchor = target.closest('a')
      const imageLink =
        !img &&
        anchor instanceof HTMLAnchorElement &&
        isImageFileHref(anchor.getAttribute('href')) &&
        (target === anchor || anchor.contains(target))

      if (!img && !imageLink) return
      stopImageNavigation(event)
      if (img) openAt(resolveLightboxSrc(img), img)
      else if (anchor) openAt(undefined, anchor)
    }

    const onDragStart = (event: DragEvent) => {
      const img = isDirectZoomableImageClick(event.target, root)
      const anchor = event.target instanceof Element ? event.target.closest('a') : null
      if (img || (anchor && isImageFileHref(anchor.getAttribute('href')))) {
        stopImageNavigation(event)
      }
    }

    root.addEventListener('click', onClick, true)
    root.addEventListener('dragstart', onDragStart, true)
    return () => {
      root.removeEventListener('click', onClick, true)
      root.removeEventListener('dragstart', onDragStart, true)
    }
  }, [contentRef, openAt])

  return {
    openHero: (event?: { isTrusted?: boolean }) => {
      if (event && event.isTrusted === false) return
      openAt(heroSrc || undefined, heroOriginEl?.current)
    },
  }
}
